import React from 'react';
import { Card } from '@/components/ui/card';
import {
  ShieldCheck, Scale, Sparkles, CalendarClock, Repeat, Info,
} from 'lucide-react';

const HARD = [
  { t: 'Jedna zmena na deň', d: 'Každý zamestnanec má max. jednu zmenu denne (ráno R, poobede P, medzizmena M, celodenná C alebo víkend SO/NE).' },
  { t: 'Presné obsadenie (žiadne preplnenie)', d: 'Počet ľudí v segmente nikdy nepresiahne požadovaný počet. Ráno = R+C, Poobede = P+C, víkend podľa potreby daného dňa. Uvoľnení ľudia majú voľno.', ref: '§85' },
  { t: 'Denný odpočinok ≥ 12 h', d: 'Medzi koncom jednej zmeny a začiatkom ďalšej musí byť aspoň 12 hodín nepretržitého odpočinku (počíta sa z reálnych časov zmien).', ref: '§92' },
  { t: 'Max. týždenný čas', d: 'Najviac 40 h/týždeň, s nadčasom do 48 h (§87). Pri nerovnomernom rozvrhnutí (§87) môže jeden týždeň byť dlhší a iný kratší (max. 12 h/deň), priemer však nesmie presiahnuť 40 h/týždeň za vyrovnávacie obdobie do 4 mesiacov.', ref: '§85, §87' },
  { t: 'Max. 13 dní po sebe', d: 'Nikto neodpracuje viac ako 13 dní v rade – 14. deň musí byť voľno (v ktoromkoľvek 14-dňovom okne max. 13 pracovných dní).' },
  { t: 'Dovolenka / absencia', d: 'Zadaná neprítomnosť (D) je fixné voľno – v daný deň sa neplánuje žiadna zmena.' },
  { t: 'Vedúci bez víkendov', d: 'Ak má vedúci/zástupca vypnuté „robí víkendy", nikdy nedostane sobotu ani nedeľu.' },
];

const SOFT = [
  { t: 'Pokryť obsadenie', w: 1000, d: 'Najvyššia priorita – minimalizovať podstav (chýbajúcich ľudí v segmente).' },
  { t: 'Dozor vedúceho', w: 1000, d: 'V každom pracovnom segmente je aspoň 1 vedúci/zástupca (aj keď sa nepočíta do počtu zamestnancov, slúži ako dozor +1).' },
  { t: 'Nepracovať 2 víkendy po sebe – konzistentne', w: 200, d: 'Cieľ je férová a KONZISTENTNÁ rotácia: ak okolnosti zavedú napr. „2 víkendy v práci a potom 3–4 voľné", platí to rovnako pre každého – nie raz tak, raz onak.' },
  { t: 'Uprednostniť celý víkend (nekúskovať)', w: 80, d: 'Ideálne dať jednému človeku CELÝ víkend (sobotu aj nedeľu), nech nerobí raz sobotu a o týždeň zas nedeľu. Sobota samostatne JE povolená (napr. 3. človek v sobotu); nedeľa bez soboty sa len mierne penalizuje.' },
  { t: 'Rotácia víkendov (férovosť)', w: 60, d: 'Vyrovnať počet odpracovaných víkendových dní medzi ľuďmi – vrátane prenosu z minulých mesiacov (kolotoč).' },
  { t: 'Vyváženie celodenných (C)', w: 40, d: 'Celodenné zmeny rozdeliť rovnomerne medzi všetkých (nie všetky na jedného).' },
  { t: 'Naplniť fond – NV je reálne voľno', w: 25, d: 'Najprv naplniť týždenný úväzok (40 h). Náhradné voľno (NV) je rovnako voľno ako X – len je označené, že kompenzuje odpracovaný víkend. Balansujeme voľná oproti nárokom za víkendy.' },
  { t: 'Nadčas', w: 20, d: 'Minimalizovať hodiny nad rámec dohodnutého fondu.' },
  { t: '2 dni voľna po celom víkende', w: 15, d: 'Kto odpracoval celý víkend, dostane v tom týždni radšej 2 dni voľna – môžu to byť ktorékoľvek dva dni (nie nutne pondelok+utorok) a nemusia byť za sebou.' },
  { t: 'Po celodennej radšej poobedná', w: 8, d: 'Po C (06–18) nasleduje skôr poobedná – viac času na zotavenie. Ak nie poobedná, tak radšej medzizmena než ranná (ranná by hrozila tesným 12h odpočinkom).' },
  { t: 'Striedanie ráno/poobede', w: 8, d: 'Týždenné striedanie ranných a poobedných zmien (band) vrátane prenosu z minulého mesiaca.' },
  { t: 'Víkend z rannej → do poobednej', w: 6, d: 'Víkend sa prevažne pracuje z ranného týždňa: ranná zmena cez týždeň + sobota + nedeľa, a nasledujúci týždeň prechod na poobednú.' },
  { t: 'Vyhnúť sa 2 celodenným po sebe', w: 6, d: 'Dve C za sebou sú POVOLENÉ (krátky/dlhý týždeň ich môže vyžadovať, výnimočne aj 3), len sa im pri striedaní R/P mierne vyhýbame.' },
  { t: 'Rovnomerné hodiny', w: 6, d: 'Vyrovnať celkové mesačné hodiny medzi zamestnancami (proti extrémom).' },
  { t: 'Šetriť celodennými (C)', w: 3, d: 'Celodenné zmeny použiť len keď reálne pomôžu pokryť prevádzku.' },
];

const NV = [
  'Cieľ §93: nepretržitý odpočinok v týždni, ktorý MUSÍ zahŕňať VOĽNÚ NEDEĽU a okolo nej aspoň 35 h súvislého odpočinku.',
  'Ako sa 35 h dosiahne: hodiny PRED nedeľou (sobota najneskôr do ~13:00) + celá voľná nedeľa, ALEBO celá voľná nedeľa + pondelok najskôr o 11:00.',
  'Náhradné voľno (NV) vzniká, keď zamestnanec odpracoval víkendový deň a v danom týždni NEMÁ takúto voľnú nedeľu s 35 h odpočinkom (napr. odpracoval celý víkend vrátane nedele).',
  'Príklad bez NV: sobota do 12:00 + voľná nedeľa = ~42 h odpočinok → §93 splnené. Príklad s NV: odpracovaná sobota aj nedeľa = 2× NV.',
  '1 nárok na NV za KAŽDÝ odpracovaný víkendový deň (sobota a nedeľa samostatne).',
  'NV sa čerpá metódou FIFO (first-in-first-out) – najstaršie nároky sa vracajú prvé. Prizná sa voľnému pracovnému dňu (Po–Pia) AŽ PO odpracovanom víkende (kompenzuje MINULÝ víkend). Je to reálne voľno, len označené, že kryje víkend.',
  'Lehota na poskytnutie: 2-dňový/4-mesačný režim alebo 24h/8-mesačný režim (§93/§87). Ak sa nárok nestihne vyčerpať, prenáša sa ďalej a kumuluje – sledujeme „Vek dlhu" a varujeme pri blížiacej sa lehote (signál podstavu).',
];

const COVERAGE = [
  'Ráno (06–13) pokrývajú zmeny R a C.',
  'Poobede (13–18) pokrývajú zmeny P a C.',
  'Medzizmena (M 09–15) sa do povinného obsadenia NEpočíta (len voliteľná, manuálna).',
  'Počty potrebných ľudí sa dajú nastaviť pre každý deň v týždni zvlášť (Po 3+2, Pia 4+3, So 3, Ne 2 …).',
  'Vedúci/zástupca sa do počtu počíta len ak má zapnuté „v počte"; inak je navyše ako dozor (+1).',
];

const CARRY = [
  'Rotácia víkendov – kto mal viac víkendov, dostane budúci mesiac menej.',
  'Striedanie ráno/poobede (band) – prvý týždeň je opačný ako posledný týždeň minulého mesiaca.',
  'Bilancia náhradného voľna (NV) – zostatok sa kumuluje a prenáša ďalej.',
];

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <Card className="p-5" data-testid={`rules-section-${title}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {children}
  </Card>
);

export default function PravidlaPage() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="pravidla-page">
      <div>
        <h2 className="text-2xl font-heading font-bold text-slate-900">Pravidlá plánovača</h2>
        <p className="text-slate-500 mt-1">
          Všetky pravidlá, ktoré engine rešpektuje pri tvorbe rozvrhu. <strong>Tvrdé</strong> pravidlá platia vždy;
          <strong> mäkké</strong> sa optimalizujú podľa váh (vyššia váha = vyššia priorita).
        </p>
      </div>

      <Section icon={ShieldCheck} title="Tvrdé pravidlá" subtitle="Musia platiť vždy – rozvrh ich nikdy neporuší">
        <ul className="space-y-3">
          {HARD.map((r) => (
            <li key={r.t} className="flex gap-3" data-testid={`hard-rule-${r.t}`}>
              <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${r.warn ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              <div>
                <div className="font-medium text-slate-800 text-sm flex items-center gap-2">
                  {r.t}
                  {r.ref && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{r.ref}</span>}
                  {r.warn && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">na zváženie</span>}
                </div>
                <div className="text-sm text-slate-500">{r.d}</div>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Sparkles} title="Mäkké pravidlá (optimalizácia)" subtitle="Engine ich vyvažuje podľa váhy – čím vyššie číslo, tým dôležitejšie">
        <ul className="space-y-3">
          {SOFT.map((r) => (
            <li key={r.t} className="flex gap-3" data-testid={`soft-rule-${r.t}`}>
              <span className="mt-0.5 text-[11px] font-mono font-bold text-slate-700 bg-slate-100 rounded px-1.5 py-0.5 shrink-0 w-14 text-center">
                {r.w}
              </span>
              <div>
                <div className="font-medium text-slate-800 text-sm">{r.t}</div>
                <div className="text-sm text-slate-500">{r.d}</div>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Scale} title="§93 – Náhradné voľno (NV)" subtitle="Kedy vzniká nárok na náhradné voľno">
        <ul className="space-y-2 list-disc pl-5 text-sm text-slate-600">
          {NV.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </Section>

      <Section icon={CalendarClock} title="Pokrytie a segmenty" subtitle="Ako sa počíta obsadenie prevádzky">
        <ul className="space-y-2 list-disc pl-5 text-sm text-slate-600">
          {COVERAGE.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </Section>

      <Section icon={Repeat} title="Prenos medzi mesiacmi (kolotoč)" subtitle="Spravodlivosť pokračuje aj cez hranicu mesiaca">
        <ul className="space-y-2 list-disc pl-5 text-sm text-slate-600">
          {CARRY.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </Section>

      <Card className="p-4 bg-slate-50 border-slate-200">
        <div className="flex gap-3 text-sm text-slate-600">
          <Info className="w-5 h-5 text-slate-400 shrink-0" />
          <p>
            Chýba vám niektoré pravidlo alebo chcete zmeniť váhu/prioritu? Napíšte mi – pravidlá sa dajú
            doladiť (napr. uprednostniť jednodňové víkendy pred celými víkendmi, aby vznikalo menej náhradného voľna).
          </p>
        </div>
      </Card>
    </div>
  );
}
